import React, { useContext, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

// components
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb'
import SalonSeoSchema from './components/SalonSeoSchema/SalonSeoSchema'
import BasicSalon from './components/BasicSalon/BasicSalon'
import ProfiSalon from './components/ProfiSalon/ProfiSalon'
import ReservationBookingModal from '../../components/ReservationBookingModal/ReservationBookingModal'
import ReservationBookingPromoModal from '../../components/ReservationBookingPromoModal/ReservationBookingPromoModal'
import SalonHead from './components/SalonHead/SalonHead'

// types
import {
	BloomreachPostSalonDetailBody,
	ConfigResponse,
	LoyaltyConfigResponse,
	PageProps,
	ReservationBookingModalInitData,
	SalonEmployeesResponse,
	SalonPageQuery,
	SalonResponse,
	SalonReviewsResponse,
	SalonServicesResponse,
	ServiceCardCallbackData
} from '../../types/types'

// utils
import createPageContainer from '../../utils/createPageContainer'
import { pushToDataLayer } from '../../utils/dataLayer'
import { getAddToCartEvent, getPageViewEvent, getReservationBeginEvent, getViewItemEvent } from '../../utils/dataLayerEvents'
import { ENVIRONMENTS, GA_EVENTS_DELAY, SALON_EMPLOYEES_LIMIT, SALON_TYPE, SCREEN_STATE_KEY } from '../../utils/enums'
import { areReservationsAllowed, getFirstLevelCategoriesNames, PAGE_LINKS } from '../../utils/helper'
import { AppContext } from '../../utils/appProvider'

// hooks
import useMessages from '../../hooks/useMessages'
import useAuthRedirect from '../../hooks/useAuthRedirect'

// styles
import * as SC from './SalonPageStyles'

type RouteProps = {
	salonSlug: string
}

type SalonPageData = {
	salonData: SalonResponse
	servicesData: SalonServicesResponse
	configData: ConfigResponse
	loyaltyConfigData: LoyaltyConfigResponse
	reviewsData: SalonReviewsResponse
	employeesData: SalonEmployeesResponse | undefined
	allowedReservations: boolean
}

type SalonPageProps = PageProps<SalonPageData, SalonPageQuery, RouteProps>

const SalonPage = (props: SalonPageProps) => {
	const { query, setQuery, salonData, servicesData, configData, reviewsData, employeesData, salonSlug, allowedReservations, setPageData } = props
	const { salon } = salonData

	const { locale } = useIntl()

	const { messages } = useMessages()
	const {
		apiBrowser,
		userInfo: { firstName, lastName, email },
		isAuthorized
	} = useContext(AppContext)

	const { snapshot } = useAuthRedirect(SCREEN_STATE_KEY.RESERVATION_SUMMARY_MODAL_SALON_CATEGORY_DETAIL)

	const [isReservationBookingPromoModalOpen, setIsReservationBookingPromoModalOpen] = useState(false)
	const [reservationBookingModalInitData, setReservationBookingModalInitData] = useState<ReservationBookingModalInitData | null>(null)

	const salonServices = getFirstLevelCategoriesNames(servicesData)
	const isBasic = salon.createType === SALON_TYPE.BASIC

	useEffect(() => {
		if (snapshot) {
			setReservationBookingModalInitData((prevState) => {
				if (!prevState) {
					return {
						...snapshot,
						bookingConfirmationData: isAuthorized
							? {
									firstName,
									lastName,
									email
								}
							: snapshot.bookingConfirmationData
					}
				}

				return null
			})
		}
	}, [snapshot, isAuthorized, firstName, lastName, email])

	// GA events
	useEffect(() => {
		const pageViewEvent = getPageViewEvent('salon', 'salons')
		pushToDataLayer(pageViewEvent)

		// timeout so the dataLayer is updated after the page_view event is sent
		setTimeout(() => {
			const viewItemEvent = getViewItemEvent({ salon })
			pushToDataLayer(viewItemEvent)
		}, GA_EVENTS_DELAY)
	}, [salon])

	// Bloomreach tracking
	useEffect(() => {
		// NOTE: no need to make this request in local development environment, since it returns CORS errors
		if (process.env.NODE_ENV === ENVIRONMENTS.development) {
			return
		}

		;(async () => {
			try {
				const { salon: salonDetail } = await apiBrowser.b2c.getSalonData(salonSlug, { headers: { 'Accept-Language': 'en' } })
				const reqBody: BloomreachPostSalonDetailBody = {
					id: salonDetail.id,
					name: salonDetail.name ?? '',
					industryIds: salonDetail.categories.map((category) => category.id),
					industryNames: salonDetail.categories.map((category) => category.name ?? category.id),
					cosmeticIds: salonDetail.cosmetics.map((cosmetic) => cosmetic.id),
					cosmeticNames: salonDetail.cosmetics.map((cosmetic) => cosmetic.name ?? cosmetic.id),
					city: salonDetail.address?.city ?? '',
					address: salonDetail.formattedAddress ?? '',
					country: salonDetail.address?.countryCode ?? ''
				}
				apiBrowser.bloomreach.postSalonDetail(reqBody)
			} catch {
				// Empty block
			}
		})()
	}, [apiBrowser.bloomreach, apiBrowser.b2c, salonSlug])

	const fetchSalonData = async () => {
		await setPageData({ dataKey: 'salonData', data: () => apiBrowser.b2c.getSalonData(salonSlug) })
	}

	const onOpenBookingModal = (serviceData?: ServiceCardCallbackData) => {
		/* Display the reservation promo modal instead of the booking modal when reservations are not allowed */
		if (!allowedReservations) {
			setIsReservationBookingPromoModalOpen(true)

			const event = getAddToCartEvent(salon)
			pushToDataLayer(event)

			return
		}
		const initialData: ReservationBookingModalInitData = {
			initIndustryID: serviceData?.industryID ?? query.fe_selectedIndustryID ?? null,
			initServiceID: serviceData?.serviceID ?? null,
			initAvResTimeSlotDayPart: query.fe_selectedAvResTimeSlotDayPart || null,
			initServiceCategoryParameterID: serviceData?.serviceCategoryParameterID || null,
			parameterSelectionData: null,
			employeeAndDateSelectionData: {
				date: query.fe_selectedAvResTimeSlotDate || undefined
			},
			employeeAndDateGroupSelectionData: {
				date: query.fe_selectedAvResTimeSlotDate || undefined
			},
			bookingConfirmationData: isAuthorized
				? {
						firstName,
						lastName,
						email
					}
				: null,
			reservationsSummaryData: null
		}
		setReservationBookingModalInitData(initialData)

		// If no service data is provided, the user opened the modal from the general button, not from a specific service card.
		if (!serviceData) {
			const event = getAddToCartEvent(salon)
			pushToDataLayer(event)
		}

		const event = getReservationBeginEvent({
			event: 'reservation_begin',
			mode: serviceData ? 'with_service' : 'blank',
			category: serviceData?.industryID,
			subcategory: serviceData?.gaSpecificData.secondLevelCategoryID,
			type: serviceData?.gaSpecificData.serviceCategoryID
		})
		pushToDataLayer(event)
	}

	// breadcrumbs
	const breadcrumbItems = [{ link: '', name: salon.name || '' }]
	return (
		<>
			<SalonHead salonSlug={salonSlug} salon={salon} salonServices={salonServices} />
			<SalonSeoSchema
				salon={salon}
				breadcrumbItems={[{ link: PAGE_LINKS['/salons'](locale), name: messages.Salons }, ...breadcrumbItems]}
				countriesData={configData.rolloutCountries}
				salonSlug={salonSlug}
			/>
			<SC.PageContainer>
				<Breadcrumb breadcrumbItems={breadcrumbItems} />

				{isBasic ? (
					<BasicSalon salonData={salon} countriesData={configData.rolloutCountries} /* loyaltyConfigData={loyaltyConfigData} */ />
				) : (
					<ProfiSalon
						salonData={salon}
						countriesData={configData.rolloutCountries}
						salonServicesData={servicesData}
						salonReviewsData={reviewsData}
						onOpenBookingModal={onOpenBookingModal}
						fetchSalonData={fetchSalonData}
						employeesData={employeesData}
						query={query}
						setQuery={setQuery}
						allowedReservations={allowedReservations}
						// loyaltyConfigData={loyaltyConfigData}
					/>
				)}
			</SC.PageContainer>

			{allowedReservations && reservationBookingModalInitData && (
				<ReservationBookingModal
					salonData={{
						id: salon.id,
						availableReservationSystem: salon.availableReservationSystem,
						images: salon.images,
						phones: salon.phones
					}}
					isOpen
					initData={reservationBookingModalInitData}
					onClose={() => setReservationBookingModalInitData(null)}
					salonServicesData={servicesData}
					configData={configData}
				/>
			)}

			{/* Display the reservation promo modal instead of the booking modal when reservations are not allowed */}
			{!allowedReservations && isReservationBookingPromoModalOpen && (
				<ReservationBookingPromoModal
					isOpen
					onClose={() => setIsReservationBookingPromoModalOpen(false)}
					availableReservation={salon.availableReservationSystem}
				/>
			)}
		</>
	)
}

// page container
const SalonPageContainer = createPageContainer<SalonPageData, SalonPageQuery, RouteProps>({
	identifier: 'SalonPage',
	PageComponent: SalonPage,
	initDefaultData: async ({ api, salonSlug, shopId }) => {
		const salonData = await api.b2c.getSalonData(salonSlug)
		const salonID = salonData.salon.id

		// Fetching salon employees with error handling to avoid breaking page load if the request fails
		const getSalonEmployees = api.b2c.getSalonEmployees(salonID, { page: 1, limit: SALON_EMPLOYEES_LIMIT }).catch(() => undefined)

		const [servicesData, reviewsData, configData, employeesData, loyaltyConfigData] = await Promise.all([
			api.b2c.getSalonServicesData(salonID),
			api.b2c.getSalonReviewsData(salonID, { page: 1 }),
			api.b2c.getConfigData(),
			getSalonEmployees,
			api.b2c.getLoyaltyConfigData()
		])

		const allowedReservations = areReservationsAllowed(configData.allowReservations, shopId)

		return { salonData, servicesData, configData, reviewsData, allowedReservations, employeesData, loyaltyConfigData }
	}
})

export default SalonPageContainer
